import { SuccessSnackBarComponent } from '@app/shared/snack-bars/success/success.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Component, ElementRef, NgZone, OnInit, ViewChild, effect, inject, untracked } from '@angular/core';
import { ScreenReaderAnnouncerService } from '@app/services/screen-reader-announcer.service';
import { LeftBarComponent } from '@app/shared/results/left-bar/left-bar.component';
import { ResultPageComponent } from '@app/shared/results/result-page/result-page.component';
import { CareerResultSchema } from '@app/schemas/career-matches';
import { CareerMatchesService } from '@app/services/api/career-matches/career-matches.service';
import { 
  bookmarkCareerMatch, 
  setAdeptIDCareerMatches, 
  setCareerGoalByOnetCode,
  setCareerMatches,
} from '@app/state/actions/careers.action';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { setResumeUpload } from '@app/state/actions/resume.action';
import { WebSocketService } from '@app/services/web-socket-service';
import { SuccessDefaultOptions } from '@app/shared/snack-bars/success/success.utils';
import { trigger, style, transition, animate } from '@angular/animations';
import { ResumeStatusService } from '@app/services/signals/resume-status-service';
import { getGrowthStatementByCategory } from '@app/utils/growth-statement';
import { formatSalary, getCareerEducation, getCareerIcon } from '@app/utils/careers';
import { BYP_PLAN_GENERATED_LOCAL_KEY, CAREER_GOAL_DETAILS_LOCAL_KEY, CAREER_GOAL_LOCAL_KEY, USER_RESUME_LOCAL_KEY } from '@app/utils/constants';
import { CareerSurveyStore } from '@app/services/signals/career-survey-service';
import { ProfileUpdateService } from '@app/services/signals/profile-update-service';
import { analytics } from '@app/events/segment/analytics';
import { EdgeAssessmentsService } from '@app/services/api/edge-assessments.service';
import { ConfigService } from '@app/services/config-service';
import { catchError, of } from 'rxjs';

@Component({
  selector: 'app-career-results',
  imports: [
    LeftBarComponent,
    ResultPageComponent,
  ],
  templateUrl: './career-results.component.html',
  styleUrl: './career-results.component.css',
  animations: [
    trigger('fadeOut', [
      transition(':leave', [
        animate('500ms ease-in', style({ opacity: 0 }))
      ])
    ]),
    trigger('slideUp', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('500ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
      ])
    ])
  ]
})
export class CareerResultsComponent implements OnInit {
  private _snackBar = inject(MatSnackBar);
  private announcer = inject(ScreenReaderAnnouncerService);
  @ViewChild('contentDiv') contentDiv!: ElementRef<HTMLDivElement>;
  private scrollTimeout: any;

  hasNewResults: boolean = false;
  resumeUpdated: boolean = false;
  itemsPerPage: number = 20;
  currentPage: number = 1;
  showLoader = true;
  loadingMessage = '';
  careers: CareerResultSchema[] = [];
  edgeAssessmentId: string = '';
  edgeCommunicationStatus: string = 'pending';
  edgeBehavioralStatus: string = 'pending';

  constructor(
    private apiService: CareerMatchesService,
    private ngZone: NgZone,
    private store: Store<{
      careerMatches: CareerResultSchema[],
    }>,
    private router: Router,
    private webSocketService: WebSocketService,
    private resumeStatusService: ResumeStatusService,
    private surveyStore: CareerSurveyStore,
    private profileService: ProfileUpdateService,
    private edgeAssessmentsService: EdgeAssessmentsService,
    private configService: ConfigService,
  ) {
    effect(() => {
      const resumeUpdated = this.resumeStatusService.resumeStatusUpdated();
      const profileUpdated = this.profileService.userProfileUpdated();
      untracked(() => {
        if (resumeUpdated) {
          this.hasNewResults = true;
          this.resumeUpdated = true;
          this.resumeStatusService.setResumeStatusUpdated(false);
        }
        if (profileUpdated) {
          this.hasNewResults = true;
          this.profileService.setUserProfileUpdated(false);
        }
        this.getMatchResults();
      });
    });
  }

  openSnackBar() {
    this._snackBar.openFromComponent(SuccessSnackBarComponent, {
      ...SuccessDefaultOptions,
      data: { 
        message: 'New match results are available.',
        linkText: 'Refresh career matches.' ,
      },
    });
  }

  ngOnInit() {
    this.fetchEdgeAssessmentStatuses();
    
    this.webSocketService.registerHandler('user_assessment_status_changed', (message) => {
    });

    this.webSocketService.registerHandler('user_assessment_scored', (message) => {
      this.ngZone.run(() => {
        this.edgeAssessmentId = message?.payload?.assessmentId || '';
        this.openSnackBar();
      });
    });  
  }

  fetchEdgeAssessmentStatuses() {
    const edgeCommunicationAssessmentId = this.configService.edgeCommunicationAssessmentId;
    const edgeBehavioralAssessmentId = this.configService.edgeBehavioralAssessmentId;

    // Fetch communication assessment status
    this.edgeAssessmentsService.getAssessmentStatus(edgeCommunicationAssessmentId).pipe(
      catchError(error => {
        if (error.status === 404) {
          this.edgeCommunicationStatus = 'not_started';
        }
        return of(null);
      })
    ).subscribe(response => {
      if (response) {
        this.edgeCommunicationStatus = response.assessmentStatus;
      }
    });

    // Fetch behavioral assessment status
    this.edgeAssessmentsService.getAssessmentStatus(edgeBehavioralAssessmentId).pipe(
      catchError(error => {
        if (error.status === 404) {
          this.edgeBehavioralStatus = 'not_started';
        }
        return of(null);
      })
    ).subscribe(response => {
      if (response) {
        this.edgeBehavioralStatus = response.assessmentStatus;
      }
    });
  }

  getMatchResults() {
    this.store.select('careerMatches').subscribe(value => this.careers = value);
    if (this.careers.length === 0 || this.hasNewResults) {
      this.showLoader = true;
      this.announcer.announce('Fetching careers that best fit you');
      this.apiService.getCareerMatches().subscribe({
        next: (data) => this.processAPIResults(data),
        error: (err) => {
          console.log(err);
          this.showLoader = false;
          this.announcer.announceAfterLoading('Failed to load career matches. Please try again.');
        }
      });
    } else {
      this.showLoader = false;
      this.announcer.announce('Careers loaded.');
    }
  }

  processAPIResults(data: any) {
    if (data.message === 'success' && data.result) {
      let results = [];
      if (data.result && data.result.results && data.result.results[0].recommendations) {
        results = data.result?.results[0].recommendations
      } else if (data.result) {
        results = data.result;
      }
      const matchResults: CareerResultSchema[] = results.map((item: any) => {
        const matchScore = item.rank >= 30 ? "low" : (
          item.rank >= 15 ? "med" : (
            item.rank >= 5 ? "h1" : "h1_plus"
          ));

        const salaryStr = formatSalary(item?.oews?.med_salary);
        const growth_category = item?.pmp?.growth_category || null;
        const careerIcon = getCareerIcon(item.onet_career_cluster[0]);
        const education = getCareerEducation(item.onet_job_zone);

        return {
          icon: careerIcon,
          title: item.onet_name,
          area: item.onet_career_cluster,
          match_score: matchScore,
          salary: salaryStr,
          growth: getGrowthStatementByCategory(growth_category),
          education: education,
          saved: false,
          rank: item.rank,
          onet_code: item.onet_code,
        }
      });

      this.store.dispatch(setCareerMatches({ payload: matchResults }));
      this.store.dispatch(setResumeUpload({ resume_upload: data.resume_upload || false }));
      this.store.dispatch(setCareerGoalByOnetCode({ onetCode: data.career_goal || "" }));
      localStorage.setItem(CAREER_GOAL_LOCAL_KEY, data.career_goal || "");
      localStorage.setItem(USER_RESUME_LOCAL_KEY, data.resume_upload ? "true" : "false");
      if (!data.career_goal) {
        localStorage.setItem(BYP_PLAN_GENERATED_LOCAL_KEY, "false");
      }

      // Track Career Available event
      this.trackCareerAvailable(data, matchResults);

      if (this.hasNewResults) {
        if (this.resumeUpdated) {
          this.showResumeUploadMessage();
          this.resumeUpdated = false;
        }
        this.hasNewResults = false;
      }
      
      if (this.surveyStore.careerSurveyTaken()) {
        this.showCareerMatchesSnackbar();
        this.surveyStore.resetSurveyTaken();
      }
    }
    this.showLoader = false;
    this.announcer.announceAfterLoading('Careers loaded.');
  }

  toggleBookmark(i: number) {
    this.store.dispatch(bookmarkCareerMatch({ index: i }));
  }

  goToCareerDetails(onetCode: string) {
    const encodedOnetCode = encodeURIComponent(onetCode);
    this.router.navigate([`/career-details/${encodedOnetCode}`]);
  }

  onScroll() {
    const el = this.contentDiv.nativeElement;
    el.classList.add('show-scrollbar');

    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      el.classList.remove('show-scrollbar');
    }, 1000);
  }

  showResumeUploadMessage() {
    this._snackBar.openFromComponent(SuccessSnackBarComponent, {
      ...SuccessDefaultOptions,
      data: { 
        message: 'Resume successfully added',
      },
      duration: 3000,
    });
  }

  showCareerMatchesSnackbar() {
    this._snackBar.openFromComponent(SuccessSnackBarComponent, {
      ...SuccessDefaultOptions,
      data: { 
        message: 'Nice! You have 5 very high matches',
      },
      duration: 4000,
    });
  }

  private trackCareerAvailable(apiData: any, matchResults: CareerResultSchema[]) {
    // Determine recommend_input based on available data
    const recommendInput: string[] = [];
    
    if (matchResults.length > 0) {
      recommendInput.push('survey');
    }
    
    if (apiData.resume_upload) {
      recommendInput.push('resume');
    }
    
    if (this.edgeCommunicationStatus === 'COMPLETED') {
      recommendInput.push('communication');
    }
    
    if (this.edgeBehavioralStatus === 'COMPLETED') {
      recommendInput.push('durable_skills');
    }
    
    // Format careers data for analytics
    const careersData = matchResults.slice(0, 20).map((career: CareerResultSchema) => ({
      onet_identifier: career.onet_code || '',
      title: career.title,
      match_level: career.match_score,
      median_salary: career.salary,
      location: apiData.location || '',
      growth_potential: career.growth
    }));
    
    analytics.track('Career Available', {
      section: 'explore',
      recommend_input: recommendInput,
      careers: careersData
    });
  }
}
